import { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Archive, RotateCcw, Percent, Users } from 'lucide-react';
import { listDiscounts, archiveDiscount, updateDiscount, discountLabel, Discount } from './api';
import DiscountForm from './DiscountForm';
import EmptyState from '../../components/EmptyState';
import { Skeleton } from '../../components/Skeleton';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

export default function DiscountsPanel() {
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState<Discount[] | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [form, setForm] = useState<{ open: boolean; discount?: Discount | null }>({ open: false });

  const reload = useCallback(() => {
    setItems(null);
    listDiscounts(showArchived).then((r) => setItems(r.data));
  }, [showArchived]);
  useEffect(() => { reload(); }, [reload]);

  async function onArchive(d: Discount) {
    if (!confirm(`¿Archivar el descuento "${d.name}"? Dejará de aparecer al asignarlo. Los ${d.student_count} alumnos que lo tienen no se tocan.`)) return;
    try { await archiveDiscount(d.discount_id); toast.success('Descuento archivado'); reload(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo archivar'); }
  }
  async function onRestore(d: Discount) {
    try { await updateDiscount(d.discount_id, { restore: true }); toast.success('Descuento restaurado'); reload(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo restaurar'); }
  }

  const visible = (items || []).filter((d) => !d.deleted_at);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-content-2">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-[rgb(var(--c-accent-solid))]" />
          Mostrar archivados
        </label>
        {isAdmin && (
          <button onClick={() => setForm({ open: true, discount: null })}
            className="flex items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-1.5 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90">
            <Plus size={16} /> Nuevo descuento
          </button>
        )}
      </div>

      {!items ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : visible.length === 0 && !showArchived ? (
        <div className="rounded-2xl border border-line bg-surface">
          <EmptyState icon={Percent} title="Sin descuentos" subtitle="Crea el primer descuento." />
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
          <table className="hidden w-full text-sm sm:table">
            <thead>
              <tr className="border-b border-line bg-surface-2 text-left text-xs text-content-3">
                <th className="px-4 py-2 font-medium">Descuento</th>
                <th className="px-4 py-2 text-right font-medium">Valor</th>
                <th className="px-4 py-2 font-medium">Aplica a</th>
                <th className="px-4 py-2 text-center font-medium">Alumnas</th>
                {isAdmin && <th className="px-2 py-2"></th>}
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.discount_id} className={`group border-t border-line transition even:bg-surface-2/40 hover:bg-surface-2 ${d.deleted_at ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-2">
                    <span className="font-medium text-content">{d.name}</span>
                    {d.code && <span className="ml-2 text-xs text-content-3">{d.code}</span>}
                    {d.deleted_at && <span className="ml-2 rounded bg-content-3/15 px-1.5 py-0.5 text-[10px] font-medium text-content-3">archivado</span>}
                  </td>
                  <td className="px-4 py-2 text-right font-semibold text-brand-blue">{discountLabel(d.value, d.is_percentage)}</td>
                  <td className="px-4 py-2 text-content-2">
                    {d.membership_name || <span className="text-content-3">General</span>}
                    {!!d.duration_months && <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">{d.duration_months} meses</span>}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span className="inline-flex items-center gap-1 text-content-2"><Users size={13} className="text-content-3" />{d.student_count}</span>
                  </td>
                  {isAdmin && (
                    <td className="px-2 py-2 text-right">
                      <div className="hover-reveal flex items-center justify-end gap-1 opacity-0 transition group-hover:opacity-100">
                        <button onClick={() => setForm({ open: true, discount: d })} title="Editar" className="rounded p-1 text-content-3 hover:text-brand-blue"><Pencil size={15} /></button>
                        {d.deleted_at
                          ? <button onClick={() => onRestore(d)} title="Restaurar" className="rounded p-1 text-content-3 hover:text-emerald-500"><RotateCcw size={15} /></button>
                          : <button onClick={() => onArchive(d)} title="Archivar" className="rounded p-1 text-content-3 hover:text-amber-500"><Archive size={15} /></button>}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Móvil */}
          <div className="divide-y divide-line sm:hidden">
            {items.map((d) => (
              <div key={d.discount_id} className={`px-4 py-3 ${d.deleted_at ? 'opacity-50' : ''}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-content">{d.name}{d.code && <span className="ml-2 text-xs text-content-3">{d.code}</span>}</div>
                    <div className="mt-0.5 text-xs text-content-3">
                      {d.membership_name || 'General'}{!!d.duration_months && ` · ${d.duration_months} meses`} · <Users size={11} className="inline" /> {d.student_count}
                    </div>
                  </div>
                  <span className="shrink-0 font-display font-bold text-brand-blue">{discountLabel(d.value, d.is_percentage)}</span>
                </div>
                {isAdmin && (
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => setForm({ open: true, discount: d })} className="flex items-center gap-1 rounded-lg border border-line px-2.5 py-1 text-xs text-content-2"><Pencil size={13} /> Editar</button>
                    {d.deleted_at
                      ? <button onClick={() => onRestore(d)} className="flex items-center gap-1 rounded-lg border border-line px-2.5 py-1 text-xs text-content-2"><RotateCcw size={13} /> Restaurar</button>
                      : <button onClick={() => onArchive(d)} className="flex items-center gap-1 rounded-lg border border-line px-2.5 py-1 text-xs text-content-2"><Archive size={13} /> Archivar</button>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {form.open && <DiscountForm discount={form.discount} onClose={() => setForm({ open: false })} onSaved={() => { setForm({ open: false }); reload(); }} />}
    </div>
  );
}
